import { Injectable } from '@angular/core';

export interface AccessibilitySettings {
  textSpacing: 0 | 1 | 2 | 3;
  dyslexiaFont: boolean;
  lineHeight: 0 | 1 | 2;
  bigText: 0 | 1 | 2 | 3;
}

export const ACCESSIBILITY_SETTINGS_KEY = 'ait-accessibility-settings';
export const ACCESSIBILITY_SETTINGS_CHANGED_EVENT = 'ait-accessibility-settings-changed';

export const DEFAULT_ACCESSIBILITY_SETTINGS: AccessibilitySettings = {
  textSpacing: 0,
  dyslexiaFont: false,
  lineHeight: 0,
  bigText: 0,
};

@Injectable({ providedIn: 'root' })
export class AccessibilitySettingsService {
  getSettings(): AccessibilitySettings {
    try {
      const raw = localStorage.getItem(ACCESSIBILITY_SETTINGS_KEY);
      if (!raw) {
        return { ...DEFAULT_ACCESSIBILITY_SETTINGS };
      }

      return this.normalizeSettings(JSON.parse(raw) as Partial<AccessibilitySettings>);
    } catch {
      return { ...DEFAULT_ACCESSIBILITY_SETTINGS };
    }
  }

  updateSettings(partial: Partial<AccessibilitySettings>): AccessibilitySettings {
    const updated = this.normalizeSettings({
      ...this.getSettings(),
      ...partial,
    });

    this.saveSettings(updated);
    return updated;
  }

  resetSettings(): AccessibilitySettings {
    const resetSettings = { ...DEFAULT_ACCESSIBILITY_SETTINGS };
    this.saveSettings(resetSettings);
    return resetSettings;
  }

  applyCurrentSettings(): void {
    this.applySettings(this.getSettings());
  }

  private saveSettings(settings: AccessibilitySettings): void {
    localStorage.setItem(ACCESSIBILITY_SETTINGS_KEY, JSON.stringify(settings));
    this.applySettings(settings);
    window.dispatchEvent(new CustomEvent(ACCESSIBILITY_SETTINGS_CHANGED_EVENT));
  }

  private applySettings(settings: AccessibilitySettings): void {
    const root = document.documentElement;

    root.setAttribute('data-a11y-text-spacing', String(settings.textSpacing));
    root.setAttribute('data-a11y-line-height', String(settings.lineHeight));
    root.setAttribute('data-a11y-big-text', String(settings.bigText));
    root.setAttribute('data-a11y-dyslexia', settings.dyslexiaFont ? 'on' : 'off');
  }

  private normalizeSettings(settings: Partial<AccessibilitySettings>): AccessibilitySettings {
    return {
      textSpacing: this.normalizeLevel(settings.textSpacing, 3) as AccessibilitySettings['textSpacing'],
      dyslexiaFont: settings.dyslexiaFont === true,
      lineHeight: this.normalizeLevel(settings.lineHeight, 2) as AccessibilitySettings['lineHeight'],
      bigText: this.normalizeLevel(settings.bigText, 3) as AccessibilitySettings['bigText'],
    };
  }

  private normalizeLevel(value: unknown, max: number): number {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= max
      ? value
      : 0;
  }
}
